import React, { useState } from 'react';
import {
    TouchableOpacity,
    StyleSheet,
    StyleProp,
    ViewStyle,
} from 'react-native';
import type { LucideIcon } from 'lucide-react-native';
import { studentTheme } from '../../theme/tokens';

export type CandyColor = 'orange' | 'green' | 'blue' | 'yellow' | 'red' | 'white';

interface CandyIconButtonProps {
    color?: CandyColor;
    size?: number;
    icon?: LucideIcon;
    customGlyph?: React.ReactNode;
    onPress?: () => void;
    onPressIn?: () => void;
    onPressOut?: () => void;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    accessibilityLabel: string;
    accessibilityHint?: string;
}

const colorMap: Record<
    CandyColor,
    { face: string; bevel: string; iconColor: string }
> = {
    orange: {
        face: studentTheme.colors.candy.orange,
        bevel: studentTheme.colors.candy.orangeBevel,
        iconColor: '#FFFFFF',
    },
    green: {
        face: studentTheme.colors.candy.green,
        bevel: studentTheme.colors.candy.greenBevel,
        iconColor: '#FFFFFF',
    },
    blue: {
        face: studentTheme.colors.candy.blue,
        bevel: studentTheme.colors.candy.blueBevel,
        iconColor: '#FFFFFF',
    },
    yellow: {
        face: studentTheme.colors.candy.yellow,
        bevel: studentTheme.colors.candy.yellowBevel,
        iconColor: '#FFFFFF',
    },
    red: {
        face: studentTheme.colors.candy.red,
        bevel: studentTheme.colors.candy.redBevel,
        iconColor: '#FFFFFF',
    },
    white: {
        face: '#FFFFFF',
        bevel: '#CBD5E1',
        iconColor: studentTheme.colors.text.dark,
    },
};

/**
 * CandyIconButton — Chunky circular tactile button.
 * Matches the warm-orange back chevron button and green fullscreen button from the reference screenshot.
 */
export const CandyIconButton: React.FC<CandyIconButtonProps> = ({
    color = 'orange',
    size = 56,
    icon: Icon,
    customGlyph,
    onPress,
    onPressIn,
    onPressOut,
    disabled = false,
    style,
    testID,
    accessibilityLabel,
    accessibilityHint,
}) => {
    const [isPressed, setIsPressed] = useState(false);
    const theme = colorMap[color];
    const bevelWidth = 4;
    const currentBevel = isPressed ? 1 : bevelWidth;
    const translateY = isPressed ? 3 : 0;
    const iconSize = Math.round(size * 0.52);

    return (
        <TouchableOpacity
            testID={testID}
            accessibilityLabel={accessibilityLabel}
            accessibilityRole="button"
            accessibilityHint={accessibilityHint}
            accessibilityState={{ disabled }}
            onPress={onPress}
            onPressIn={() => {
                setIsPressed(true);
                onPressIn?.();
            }}
            onPressOut={() => {
                setIsPressed(false);
                onPressOut?.();
            }}
            disabled={disabled}
            activeOpacity={0.9}
            style={[
                styles.button,
                {
                    width: size,
                    height: size,
                    minWidth: size,
                    minHeight: size,
                    backgroundColor: disabled ? '#94A3B8' : theme.face,
                    borderBottomColor: disabled ? '#64748B' : theme.bevel,
                    borderBottomWidth: currentBevel,
                    transform: [{ translateY }],
                },
                style,
            ]}
        >
            {customGlyph ? (
                customGlyph
            ) : Icon ? (
                <Icon size={iconSize} color={disabled ? '#F1F5F9' : theme.iconColor} strokeWidth={3.5} />
            ) : null}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    button: {
        borderRadius: studentTheme.radii.round,
        alignItems: 'center',
        justifyContent: 'center',
        borderTopWidth: 1.5,
        borderTopColor: 'rgba(255, 255, 255, 0.5)',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255, 255, 255, 0.25)',
        borderRightWidth: 1,
        borderRightColor: 'rgba(255, 255, 255, 0.25)',
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.18,
        shadowRadius: 5,
        elevation: 5,
    },
});
